import React from 'react';
import { format } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Cell,
  Legend,
} from 'recharts';
import { BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { formatNumber } from './constants';

const EngagementTab = ({ analytics, platformData }) => (
  <TabsContent value="engagement" className="space-y-6">
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Engagement by Platform */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Platform Engagement</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {platformData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platformData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                  <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={80} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <Bar dataKey="value" name="Engagement" radius={[0, 4, 4, 0]}>
                    {platformData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                No platform data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Engagement Breakdown Over Time */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Engagement Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics?.trend_data?.slice(-14) || []}>
                <defs>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  fontSize={11}
                  tickFormatter={(value) => format(new Date(value), 'MMM d')}
                />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                />
                <Legend />
                <Area type="monotone" dataKey="clicks" stroke="#F59E0B" fillOpacity={1} fill="url(#colorClicks)" name="Clicks" />
                <Area type="monotone" dataKey="engagement" stroke="#8B5CF6" fillOpacity={0.5} fill="#8B5CF6" name="Engagement" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Platform Stats Table */}
    <Card className="border-slate-100">
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Platform Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        {platformData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Platform</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Posts</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Reach</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Impressions</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Engagement</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Clicks</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Eng. Rate</th>
                </tr>
              </thead>
              <tbody>
                {platformData.map((platform) => (
                  <tr key={platform.name} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: platform.color }} />
                        <span className="font-medium text-slate-900">{platform.name}</span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 text-slate-600">{platform.posts}</td>
                    <td className="text-right py-3 px-4 text-slate-600">{formatNumber(platform.reach)}</td>
                    <td className="text-right py-3 px-4 text-slate-600">{formatNumber(platform.impressions)}</td>
                    <td className="text-right py-3 px-4 text-slate-600">{formatNumber(platform.value)}</td>
                    <td className="text-right py-3 px-4 text-slate-600">{formatNumber(platform.clicks)}</td>
                    <td className="text-right py-3 px-4">
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-600">
                        {platform.reach > 0 ? ((platform.value / platform.reach) * 100).toFixed(1) : 0}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500">
            <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p>No analytics data available yet.</p>
            <p className="text-sm">Publish some posts to see your performance metrics.</p>
          </div>
        )}
      </CardContent>
    </Card>
  </TabsContent>
);

export default EngagementTab;
